import * as React from "react";

import { cn } from "@/lib/ui/cn";

type ContainerProps = React.HTMLAttributes<HTMLDivElement> & {
  children: React.ReactNode;
  className?: string;
  /**
   * Whether the container should have full-width background support.
   * When true, the outer element extends full-width for backgrounds/images,
   * while content remains constrained within the container width.
   */
  fullWidthBackground?: boolean;

  /**
   * Whether the content should also extend full-width (not just the background).
   * When true, content is not constrained by container max-width.
   * Only effective when fullWidthBackground is also true.
   */
  fullWidthContent?: boolean;

  /**
   * Additional className for the inner content wrapper.
   * Only used when fullWidthBackground is true.
   * Useful for overriding padding or adding custom styles to the content area.
   */
  innerClassName?: string;

  /**
   * The element to render as (e.g. "div", "section", "article", etc.).
   */
  as?: React.ElementType;
};

/**
 * Centralized container component that provides consistent max-width, padding, and margins.
 * - Max-width: 1440px at 2xl breakpoint
 * - Padding: px-padding-global on left and right
 * - Centered with mx-auto
 *
 * When fullWidthBackground is true:
 * - Outer element extends full-width for backgrounds/images
 * - Content is constrained within container width (unless fullWidthContent is also true)
 */
const Container = React.forwardRef<HTMLDivElement, ContainerProps>(
  (
    {
      children,
      className,
      fullWidthBackground = false,
      fullWidthContent = false,
      innerClassName,
      as: Component = "div",
      ...props
    },
    ref,
  ) => {
    // When fullWidthBackground is true, create a two-layer structure:
    // - Outer element: full-width for backgrounds
    // - Inner wrapper: constrained content width (unless fullWidthContent is true)
    if (fullWidthBackground) {
      return (
        <Component ref={ref} className={cn("w-full", className)} {...props}>
          <div
            className={cn(
              fullWidthContent ? "w-full" : "container mx-auto",
              "px-padding-global",
              innerClassName,
            )}
          >
            {children}
          </div>
        </Component>
      );
    }

    // Default behavior: single element with constrained width
    return (
      <Component
        ref={ref}
        className={cn("px-padding-global container mx-auto", className)}
        {...props}
      >
        {children}
      </Component>
    );
  },
);
Container.displayName = "Container";

export { Container };
